import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions, takeCurrentOfferState, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { min } from 'lib/array/arrayUtils'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import OfferDurationOptions from '../../Duration/OfferDurationOptions'
import { OfferDurationOption } from '../../DurationTabs/OfferDurationTabs'
import OfferCalendarDurationModal from './OfferCalendarDurationModal'

interface Props {
  duration: number
  durationOptions: Array<OfferDurationOption>
  open: boolean
  variant?: 'dropdown' | 'modal'
  offerId?: string
  isSelected?: boolean
  bundleOfferId?: string
}

function OfferCalendarDurationSelect(props: Props) {
  const {
    duration,
    durationOptions,
    open,
    variant = 'dropdown',
    offerId,
    isSelected,
    bundleOfferId,
  } = props
  const dispatch = useOfferPageDispatch()
  const durationTriggerRef = useRef(null)

  const onToggleOpen = useCallback(() => {
    if (offerId) {
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: offerId })
    }
    dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
  }, [dispatch, offerId])

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeSubFilter })
  }, [dispatch])

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const minDuration = useMemo(() => {
    return min(durationOptions, duration => duration.duration)?.duration!
  }, [durationOptions])
  const onDurationSelect = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenDuration)
  }, [onUserAnalyticsAction])

  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (variant === 'modal' && open) {
      showModal(<OfferCalendarDurationModal />).then(() => onClose())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variant])

  return (
    <>
      <FilterChip
        size="medium"
        ref={durationTriggerRef}
        onClick={onToggleOpen}
        endIcon={variant === 'dropdown' ? <DropdownChevron open={open} /> : undefined}
        selected={isBundleAndSaveV2Enabled() && bundleOfferId ? open && isSelected : open}
      >
        {pluralizeToString('night', duration)}
      </FilterChip>
      {variant === 'dropdown' && <DropdownList
        size="M"
        open={open}
        title="How long do you want to stay?"
        subtitle={minDuration > 1 ? `Minimum stay for this offer is ${pluralizeToString('night', minDuration)}` : undefined}
        anchorRef={durationTriggerRef}
        triggerRef={durationTriggerRef}
        onClose={onClose}
        portaled
      >
        <OfferDurationOptions onSelect={onDurationSelect} onClose={onClose} />
      </DropdownList>}
    </>
  )
}

export default withOfferPageAndReduxState((state, appState, props: Props) => {
  const currentState = props.offerId ? takeOfferState(state, props.offerId) : takeCurrentOfferState(state)

  const isBundleOffer = isBundleAndSaveV2Enabled() && state.offer?.type === 'bundle_and_save'

  return {
    duration: currentState.duration,
    durationOptions: getDurationOptions(currentState, appState),
    open: currentState.activeSubFilter === 'duration' && (isBundleOffer ? props.isSelected : true),
    bundleOfferId: currentState.bundleOffer?.id,
  }
}, OfferCalendarDurationSelect)
